/**
 * Root App component for the Smart Race Strategy application
 * This component serves as the main container with tab navigation
 */

import { useState } from "react";
import { HomePage } from "./pages/HomePage";
import { LiveTelemetryPage } from "./pages/LiveTelemetryPage";
import { TelemetryAnalyzerPage } from "./pages/TelemetryAnalyzerPage";
import type { RacePlan, StaticStrategyInput } from "./utils/staticStrategyPlanner";

type Tab = 'strategy' | 'telemetry' | 'analyzer';

/**
 * App component - The main application wrapper with tab navigation
 * @returns JSX element containing the tab navigation and active page
 */
function App() {
  const [activeTab, setActiveTab] = useState<Tab>('strategy');
  
  // Selected race plan — shared between Strategy (planner) and Live Telemetry (tracker)
  const [selectedPlan, setSelectedPlan] = useState<RacePlan | null>(null);
  const [selectedPlanInput, setSelectedPlanInput] = useState<StaticStrategyInput | null>(null);

  const handleSelectPlan = (plan: RacePlan, input: StaticStrategyInput) => {
    setSelectedPlan(plan);
    setSelectedPlanInput(input);
    // Persist via API so the overlay (separate Electron window) can read it
    fetch('/api/plan', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ plan, input }),
    }).catch(() => {});
  };

  const handleClearPlan = () => {
    setSelectedPlan(null);
    setSelectedPlanInput(null);
    fetch('/api/plan', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ plan: null, input: null }),
    }).catch(() => {});
  };

  return (
    <div className="App">
      {/* Tab Navigation */}
      <div className="bg-slate-800 border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex space-x-1">
            <button
              onClick={() => setActiveTab('strategy')}
              className={`px-6 py-4 font-semibold text-sm transition-colors ${
                activeTab === 'strategy'
                  ? 'bg-slate-900 text-white border-b-2 border-blue-500'
                  : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
              }`}
            >
              Race Strategy
            </button>
            <button
              onClick={() => setActiveTab('telemetry')}
              className={`px-6 py-4 font-semibold text-sm transition-colors relative ${
                activeTab === 'telemetry'
                  ? 'bg-slate-900 text-white border-b-2 border-blue-500'
                  : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
              }`}
            >
              Live Telemetry
              {selectedPlan && (
                <span className="ml-2 inline-block w-2 h-2 rounded-full bg-blue-400 animate-pulse" title="Plan active" />
              )}
            </button>
            <button
              onClick={() => setActiveTab('analyzer')}
              className={`px-6 py-4 font-semibold text-sm transition-colors ${
                activeTab === 'analyzer'
                  ? 'bg-slate-900 text-white border-b-2 border-blue-500'
                  : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
              }`}
            >
              Telemetry Analyzer
            </button>
          </div>
        </div>
      </div>

      {/* Active Page Content */}
      {activeTab === 'strategy' && (
        <HomePage 
          onSelectPlan={handleSelectPlan}
          selectedPlanId={selectedPlan?.id ?? null}
        />
      )}
      {activeTab === 'telemetry' && (
        <LiveTelemetryPage 
          selectedPlan={selectedPlan}
          selectedPlanInput={selectedPlanInput}
          onClearPlan={handleClearPlan}
        />
      )}
      {activeTab === 'analyzer' && <TelemetryAnalyzerPage />}
    </div>
  );
}

export default App;
